import React, { useState } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';
import { StaticAnalysis } from './AnalysisTypes/StaticAnalysis';
import { DynamicAnalysis } from './AnalysisTypes/DynamicAnalysis';
import { SeismicAnalysis } from './AnalysisTypes/SeismicAnalysis';
import { ResultsVisualization } from './ResultsVisualization/ResultsVisualization';
import { useAnalysisStore } from '../../store/useAnalysisStore';
import { useModelStore } from '../../store/useModelStore';
import { useUIStore } from '../../store/useUIStore';

interface AnalysisPanelProps {
  isVisible?: boolean;
  onClose?: () => void;
}

export const AnalysisPanel: React.FC<AnalysisPanelProps> = ({ 
  isVisible = true, 
  onClose 
}) => {
  const [activeTab, setActiveTab] = useState<'static' | 'dynamic' | 'seismic' | 'results'>('static');
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  
  const { 
    isRunning, 
    isAnalyzing, 
    progress, 
    analysisResults: storeResults,
    runAnalysis,
    clearResults 
  } = useAnalysisStore();
  
  const { nodes, beams } = useModelStore();
  const { showNotification } = useUIStore();

  const handleAnalysisRun = async (type: string) => {
    if (beams.length === 0 || nodes.length === 0) {
      showNotification('warning', 'No Model Data', 'Please create a model before running analysis');
      return;
    }

    try {
      await runAnalysis(type);
      setAnalysisResults(storeResults);
      showNotification('success', 'Analysis Complete', `${type} analysis completed successfully`);
    } catch (error) {
      showNotification('error', 'Analysis Failed', 'Analysis encountered an error');
    }
  };

  const handleVisualizationChange = (config: any) => {
    // console.log('Visualization config updated:', config);
    // Handle visualization updates
  };

  if (!isVisible) return null;

  return (
    <div className="analysis-panel bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Structural Analysis
        </h2>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ×
          </button>
        )}
      </div>

      {/* Progress Bar */}
      {isRunning && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Analysis Progress
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {progress}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {[
          { id: 'static', label: 'Static', icon: '📊' },
          { id: 'dynamic', label: 'Dynamic', icon: '⚡' },
          { id: 'seismic', label: 'Seismic', icon: '🌍' },
          { id: 'results', label: 'Results', icon: '📈' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-b-2 border-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4">
        {activeTab === 'static' && (
          <StaticAnalysis onAnalysisRun={handleAnalysisRun} />
        )}
        
        {activeTab === 'dynamic' && (
          <DynamicAnalysis onAnalysisRun={handleAnalysisRun} />
        )}
        
        {activeTab === 'seismic' && (
          <SeismicAnalysis />
        )}
        
        {activeTab === 'results' && (
          <ResultsVisualization 
            analysisResults={analysisResults}
            onVisualizationChange={handleVisualizationChange}
          />
        )}
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={clearResults}
          className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Clear Results
        </button>
        
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('results')}
            className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            View Results
          </button>
          <button
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
          >
            Export Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalysisPanel; 